import { useState } from "react";
import { Link, useRouter } from "@tanstack/react-router";
import { AlertTriangle, RefreshCw, ArrowLeft, LifeBuoy } from "lucide-react";

type Action = { to: string; label: string; variant?: "primary" | "ghost" };

export interface RouteErrorStateProps {
  /** Kort huvudmeddelande, t.ex. "Kunde inte hämta skolenheten". */
  title: string;
  /** Kontext för användaren om vad som gick fel och vad de kan göra. */
  description?: string;
  /** Ursprungligt felobjekt — teknisk detalj visas som collapsible. */
  error?: unknown;
  /** Länkar som visas som fallback när användaren vill navigera vidare. */
  fallbackActions?: Action[];
  /** Extra fallback-innehåll (t.ex. senast sedda skolor, populära länkar). */
  children?: React.ReactNode;
}

/**
 * Delad felyta för TanStack `errorComponent`. Ger användaren:
 *   1. Tydligt, mänskligt felmeddelande.
 *   2. Retry-knapp som kör `router.invalidate()` och visar laddningsstatus.
 *   3. Fallback-länkar och valfritt fallback-innehåll så sidan aldrig känns död.
 *   4. Kollapsad teknisk detalj för support/felsökning.
 */
export function RouteErrorState({
  title,
  description,
  error,
  fallbackActions = [
    { to: "/", label: "Till startsidan", variant: "ghost" },
    { to: "/skolor", label: "Sök i registret", variant: "primary" },
  ],
  children,
}: RouteErrorStateProps) {
  const router = useRouter();
  const [retrying, setRetrying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  const retry = async () => {
    setRetrying(true);
    try {
      await router.invalidate();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <section
      role="alert"
      aria-live="polite"
      className="mx-auto max-w-3xl px-5 py-section"
    >
      <div className="rounded-card border border-border bg-surface p-card shadow-1 p-card">
        <div className="flex items-start gap-4">
          <span
            aria-hidden
            className="grid h-12 w-12 shrink-0 place-items-center rounded-card bg-primary-soft text-primary"
          >
            <AlertTriangle className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="font-display text-h1 leading-tight">{title}</h1>
            <p className="mt-2 text-ink-muted">
              {description ??
                "Något gick fel när vi hämtade uppgifterna. Det kan bero på ett tillfälligt avbrott mot Skolverkets register eller en långsam anslutning. Försök igen om en stund — dina val och sparade skolor finns kvar."}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={retry}
            disabled={retrying}
            aria-busy={retrying}
            className="inline-flex items-center gap-2 rounded-card bg-primary px-4 py-2.5 text-small font-semibold text-primary-foreground transition hover:bg-primary-hover disabled:opacity-70"
          >
            <RefreshCw
              className={`h-4 w-4 ${retrying ? "animate-spin" : ""}`}
              aria-hidden
            />
            {retrying ? "Försöker igen…" : "Försök igen"}
          </button>
          {fallbackActions.map((a) =>
            a.variant === "primary" ? (
              <Link
                key={a.to}
                to={a.to}
                className="inline-flex items-center gap-2 rounded-card bg-ink px-4 py-2.5 text-small font-semibold text-surface transition hover:opacity-90"
              >
                {a.label}
              </Link>
            ) : (
              <Link
                key={a.to}
                to={a.to}
                className="inline-flex items-center gap-2 rounded-card border border-border bg-surface px-4 py-2.5 text-small font-semibold hover:bg-muted"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
                {a.label}
              </Link>
            ),
          )}
        </div>

        {message && (
          <div className="mt-6 rounded-card border border-border bg-muted/40 p-card text-small">
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="inline-flex items-center gap-2 font-semibold text-ink-muted hover:text-ink"
              aria-expanded={showDetails}
            >
              <LifeBuoy className="h-4 w-4" aria-hidden />
              {showDetails ? "Dölj tekniska detaljer" : "Visa tekniska detaljer"}
            </button>
            {showDetails && (
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-small text-ink-muted">
                {message}
              </pre>
            )}
          </div>
        )}
      </div>

      {children && (
        <div className="mt-8">
          <h2 className="font-display text-h4 text-ink-muted">Under tiden</h2>
          <div className="mt-3">{children}</div>
        </div>
      )}
    </section>
  );
}
